import React from "react"
import PropTypes from "prop-types"
import styled, { css } from "styled-components"
import { Link } from "gatsby"
import { breakpoints } from "../../../theme"
import { buttonStyle } from "@bw/bits"

const TeaserStyle = css`
  text-decoration: none;
  width: 100%;
  display: grid;
  grid-gap: 5px;
  color: var(--font-colors);
  cursor: pointer;
  position: relative;
`

const Image = styled.div`
  > * {
    position: relative;
    height: 91px;
    width: 91px;
    object-fit: cover;

    @media (min-width: ${breakpoints.medium}px) {
      height: 175px;
      width: 100%;
      clip-path: polygon(
        0 0,
        100% 0,
        100% 100%,
        87% 100%,
        87% calc(100% - 40px),
        0 calc(100% - 40px)
      );
    }
  }
  + div {
    @media (min-width: ${breakpoints.medium}px) {
      margin-top: -43px;
    }
  }
`

const StyledLink = styled(Link)`
  ${TeaserStyle}
`
const StyledA = styled.a`
  ${TeaserStyle}
`
const StyledDiv = styled.div`
  ${TeaserStyle}
`
const TeaserBody = styled.div`
  position: relative;
  z-index: 4;
  display: grid;
  flex-direction: row;
  align-items: center;
  gap: 5%;
  grid-template-columns: 1fr 4fr;

  @media (min-width: ${breakpoints.medium}px) {
    flex-direction: column;
    display: flex;
    gap: 0%;
  }
`

const Small = styled.div`
  font-weight: 300;
  font-size: 15px;
  line-height: 176.3%;
  margin-bottom: 0.3em;

  @media (min-width: ${breakpoints.medium}px) {
    font-size: ${props => (props.smaller ? "14px" : "15px")};
  }
`

const Title = styled.div`
  font-family: "Ginto", georgia, serif;
  font-weight: 500;
  font-weight: normal;
  font-size: 14px;
  margin: 0;
  line-height: 152.9%;

  &:first-child {
    max-width: 85%;
  }

  @media (min-width: ${breakpoints.medium}px) {
    margin: 15px 0px 10px 0px;
    font-size: ${props => (props.smaller ? "15px" : "17px")};
  }
`

const Excerpt = styled.div`
  display: none;
  font-style: normal;
  font-weight: 300;
  font-size: ${props => (props.smaller ? "14px" : "16px")};
  line-height: 176.3%;

  @media (min-width: ${breakpoints.medium}px) {
    display: block;
    margin-top: 0.8em;
  }
`

const Padding = styled.div`
  width: 100%;
`

const Button = styled.div`
  ${buttonStyle}
  margin: 25px 0 0 0;
`

const Teaser = ({
  suptitle,
  title,
  excerpt,
  to,
  href,
  onClick,
  image,
  smaller,
  buttonLabel,
}) => {
  const Card = () => (
    <TeaserBody>
      <Image>
        {image ? image : <img src="/placeholder-teaser.jpg" alt="" />}
      </Image>
      <Padding>
        {title && (
          <Title smaller={smaller}>
            {suptitle && <Small smaller={smaller}>{suptitle}</Small>}
            {title}
          </Title>
        )}

        {excerpt && <Excerpt smaller={smaller}>{excerpt}</Excerpt>}
        {buttonLabel && (
          <Button $fill $primary>
            {buttonLabel}
          </Button>
        )}
      </Padding>
    </TeaserBody>
  )

  switch (true) {
    case to !== null:
      return (
        <StyledLink to={to}>
          <Card />
        </StyledLink>
      )

    case href !== null:
      return (
        <StyledA href={href} target="_blank">
          <Card />
        </StyledA>
      )

    case onClick !== null:
      return (
        <StyledDiv onClick={onClick}>
          <Card />
        </StyledDiv>
      )

    default:
      throw new Error(
        "Unsupported type of teaser. Should use one of these props: to, href or onClick"
      )
  }
}

export { Teaser }

Teaser.propTypes = {
  image: PropTypes.element,
  suptitle: PropTypes.string,
  title: PropTypes.string.isRequired,
  excerpt: PropTypes.string,
  buttonLabel: PropTypes.string,
  onClick: PropTypes.func,
  href: PropTypes.string,
  to: PropTypes.string,
  smaller: PropTypes.bool,
}

Teaser.defaultProps = {
  smaller: false,
}
